import { Dispatch, SetStateAction, useContext, useEffect, useMemo, useReducer, useState } from 'react'
import {
  WppActionButton,
  WppButton,
  WppIconAdd,
  WppIconTrash,
  WppInput,
  WppTextareaInput,
  WppTypography,
} from '@wppopen/components-library-react'
import TextAreaWithIcon from 'components/TextAreaWithIcon/TextAreaWithIcon'
import KeywordInput from 'components/FormComponents/KeywordInput/KeywordInput'
import FormAccordionSection from '../../FormAccordionSection/FormAccordionSection'
import { getType, getTypeKey } from 'utils'
import editProductFormReducer, {
  defaultEditProductErrorObject,
  editProductFormHelperFunctions,
  EditProductFormReducerState,
  initialEditProductFormReducerState,
} from 'reducers/EditProductReducer/EditProductReducer'
import useWarningChanging from 'hooks/useWarningChanging'
import { attributesFields, type ClaimsTypes, extractClaims, mainRequiredFields } from './utils'
import { renderInputIcon, renderMessage, renderMessageType } from '../form-utils'
import type { ProductsPartialUpdatePayload } from '@contracts'
import type {
  StateToMultiInputFieldMap,
  StateToSingleInputFieldMap,
  StateToSingleSelectFieldMap,
} from 'reducers/FormReducer/FormReducer'
import type {
  WppInputCustomEvent,
  WppTextareaInputCustomEvent,
} from '@wppopen/components-library/dist/types/components'
import type {
  InputChangeEventDetail,
  SelectChangeEventDetail,
  TextareaInputChangeEventDetail,
} from '@wppopen/components-library'
import type { ProductsDetailDataV5 } from 'utils/utils.d'
import { GenericModal } from 'components/Modals'
import { ERROR_MESSAGES } from 'utils/constants'
import ProjectPageContext from 'context/projectContext/projectContext'
import usePermissions from 'hooks/usePermissions'
import latestGpcVersion from '@cli/gpc/FE_GPC_2024_12_2-converted-20250401-1743497076125-level_4.json'
import CustomSelect from 'components/FormComponents/CustomSelect/CustomSelect'
import styles from './ProductDataForm.module.scss'

interface ProductDataFormProps {
  isFormActive: boolean
  productData: ProductsDetailDataV5
  areActionsVisible?: boolean
  isApiLoading?: boolean
  setHasUnsavedChanges?: Dispatch<SetStateAction<boolean>>
  tabIconState?: Dispatch<SetStateAction<boolean[]>>
  onFormEdit?: () => void
  onFormCancel?: () => void
  onFormSubmit?: (data: ProductsPartialUpdatePayload) => Promise<void>
}

export default function ProductDataForm({
  productData,
  isFormActive,
  areActionsVisible,
  isApiLoading,
  setHasUnsavedChanges,
  tabIconState,
  onFormEdit,
  onFormCancel,
  onFormSubmit,
}: ProductDataFormProps) {
  const { keywords } = productData
  const [claims, setClaims] = useState<ClaimsTypes[]>([])
  const InitialProductData = useMemo(() => initialEditProductFormReducerState(productData), [productData])
  const formReducer = useReducer(editProductFormReducer, InitialProductData)
  const [isProductDataGood, setIsProductDataGood] = useState(false)
  const [isErrorForm, setIsErrorForm] = useState(false)
  const [formReducerState, formReducerDispatch] = formReducer
  const {
    reference_name,
    product_id,
    category,
    brand,
    reference_image_url,
    product_name,
    product_types,
    range,
    product_description,
    product_features,
    instructions,
    quantity,
    size,
    colour,
    flavour,
    format,
    formulation,
    product_dimensions,
    pack_count,
    model_number,
  } = formReducerState

  const { clientDetails } = useContext(ProjectPageContext)

  const clientId = clientDetails?.id

  const { clientLevelPermissions } = usePermissions({
    clientId,
    permissionSubset: ['PDP_APP_PATCH_PRODUCTS_BY'],
  })

  const [canPatchProduct] = clientLevelPermissions

  const optionsData = useMemo(() => {
    return latestGpcVersion.map(item => {
      return { label: item.Code, title: item.Title, breadcrumbs: item.Definition }
    })
  }, [latestGpcVersion])

  const { resetForm, updateSingleInput: updateInput, updateSelect, handleError } = editProductFormHelperFunctions
  const requiredFields = useMemo(() => {
    // Extract the requiredField array from defaultEditProductErrorObject
    const requiredFields = defaultEditProductErrorObject.requiredField || []
    // Map through the array to get an array of IDs
    const ids = requiredFields.map(({ id }) => id)
    // Filter out duplicate IDs by checking their first occurrence index
    const uniqueIds = ids.filter((id, index, array) => array.indexOf(id) === index)
    // If there are no unique IDs, return an empty array
    return uniqueIds
  }, [defaultEditProductErrorObject.requiredField])
  const keyphrasesWithDisclaimers = useMemo(() => {
    return claims?.reduce((acc, item) => {
      const { claim, disclaimer } = item
      if (claim !== '' || disclaimer !== '') {
        acc.push(claim, disclaimer)
      }
      return acc
    }, [] as string[])
  }, [claims])

  // unsaved changes section
  const isFormUnsaved = useMemo(() => {
    let hasUnsavedChanges = false

    // only when form is active because when the form is not active or it gets out of edit mode it should reset the changes
    if (isFormActive) {
      // if keyphrases_with_disclaimers is not even then add an empty string to compare
      const correct_keyphrases_with_disclaimers =
        keywords?.keyphrases_with_disclaimers && keywords?.keyphrases_with_disclaimers?.length % 2 === 0
          ? keywords?.keyphrases_with_disclaimers
          : [...keywords?.keyphrases_with_disclaimers!, '']

      hasUnsavedChanges =
        Object.keys(InitialProductData).some(key => {
          const id = key as keyof EditProductFormReducerState
          return JSON.stringify(InitialProductData[id].value) !== JSON.stringify(formReducerState[id].value)
        }) || JSON.stringify(keyphrasesWithDisclaimers) !== JSON.stringify(correct_keyphrases_with_disclaimers)
    }

    return hasUnsavedChanges
  }, [isFormActive, keyphrasesWithDisclaimers, InitialProductData, formReducerState])
  useWarningChanging(isFormUnsaved, setHasUnsavedChanges)

  // claims section
  useEffect(() => {
    // get the claims
    const { keywords } = productData
    const claimsArray = extractClaims(keywords?.keyphrases_with_disclaimers)
    setClaims(claimsArray)

    // update the tab icon state
    if (tabIconState) {
      // if any value has no value then tab icon should be shown as warning
      const isWarning = requiredFields.some(
        field => !InitialProductData[field as keyof EditProductFormReducerState].value,
      )
      tabIconState(pev => {
        return [isWarning, ...pev.slice(1)]
      })
    }
  }, [productData])

  // form actions section
  const onInputChange = (
    e: WppInputCustomEvent<InputChangeEventDetail> | WppTextareaInputCustomEvent<TextareaInputChangeEventDetail>,
  ) => {
    const { target } = e
    const { value, id: key } = target
    const id = key as keyof EditProductFormReducerState

    formReducerDispatch(updateInput(key as StateToSingleInputFieldMap<'EditProductFormReducerState'>, value))
    // if id matches the required fields and there is error then remove the error
    const isRequiredField = mainRequiredFields.includes(id)
    if (isRequiredField && formReducerState[id].error) {
      formReducerDispatch(handleError(id, undefined))
    } else if (isRequiredField && !value) {
      formReducerDispatch(handleError(id, ERROR_MESSAGES.ESSENTIAL_FIELD))
    }
  }

  const handleSelect = (e: CustomEvent<SelectChangeEventDetail>, fieldName: keyof EditProductFormReducerState) => {
    e.preventDefault()
    const { value } = e.target as HTMLWppSelectElement

    formReducerDispatch(updateSelect(fieldName as StateToSingleSelectFieldMap<'EditProductFormReducerState'>, value))

    const isRequiredField = mainRequiredFields.includes(fieldName)
    if (isRequiredField && formReducerState[fieldName].error) {
      formReducerDispatch(handleError(fieldName, undefined))
    } else if (isRequiredField && !value) {
      formReducerDispatch(handleError(fieldName, ERROR_MESSAGES.ESSENTIAL_FIELD))
    }
  }

  const handleFormSubmit = () => {
    let hasErrors = false
    // check all required fields values and if they are empty, dispatch the error
    mainRequiredFields.forEach(field => {
      const id = field as keyof EditProductFormReducerState
      if (!formReducerState[id].value) {
        hasErrors = true
        formReducerDispatch(handleError(id, ERROR_MESSAGES.ESSENTIAL_FIELD))
      }
    })

    if (hasErrors) {
      setIsErrorForm(true)
    }
    if (hasErrors || !isFormUnsaved) return
    setIsProductDataGood(true)
  }

  const handleSaveProduct = async () => {
    const newProductData: ProductsPartialUpdatePayload = {
      project_id: productData.project_id,
      reference_name: reference_name.value,
      product_id: product_id.value,
      gpc_code: Number(category?.value),
      reference_image_url: reference_image_url.value,
      attributes: [],
      keywords: {
        priority: formReducerState.priority.value,
        required: formReducerState.required.value,
        optional: formReducerState.optional.value,
        omit: formReducerState.omit.value,
        keyphrases_required: formReducerState.keyphrases_required.value,
        keyphrases_optional: formReducerState.keyphrases_optional.value,
        keyphrases_with_disclaimers: keyphrasesWithDisclaimers,
      },
    }
    const wasCategoryEdited = InitialProductData.category?.value !== category?.value
    if (!wasCategoryEdited) delete newProductData.gpc_code

    // add attributes to the newProductData
    attributesFields.forEach(field => {
      const id = field as keyof EditProductFormReducerState
      if (formReducerState[id].value) {
        newProductData.attributes?.push({
          name: field,
          value: formReducerState[id].value as string,
        })
      }
    })

    // update data
    try {
      if (onFormSubmit) await onFormSubmit(newProductData)
      setIsProductDataGood(false)
    } catch (err) {
      throw err
    }
  }

  return (
    <div className={styles.formContainer}>
      <form className={styles.actions} data-testid="product-data-form">
        {canPatchProduct && areActionsVisible && (
          <>
            {isFormActive ? (
              <>
                <WppActionButton
                  data-testid="cancel"
                  onClick={() => {
                    formReducerDispatch(resetForm(InitialProductData))
                    const claimsArray = extractClaims(productData.keywords?.keyphrases_with_disclaimers)
                    setClaims(claimsArray)
                    if (onFormCancel) onFormCancel()
                  }}
                >
                  Cancel
                </WppActionButton>
                <WppButton onClick={handleFormSubmit} data-testid="save-edit">
                  Save edits
                </WppButton>
              </>
            ) : (
              <WppButton onClick={onFormEdit} data-testid="edit">
                Edit
              </WppButton>
            )}
          </>
        )}
      </form>
      <div className={styles.formInner}>
        <div className={styles.halfWidth}>
          <WppInput
            labelConfig={{
              text: 'Reference name',
              icon: !isFormActive ? undefined : 'wpp-icon-info',
              description: 'Used internally to quickly find the product. This does not get used for AI generation.',
            }}
            required
            data-testid="reference-name"
            id={getType<StateToSingleInputFieldMap<'EditProductFormReducerState'>>('reference_name')}
            value={reference_name.value}
            disabled={!isFormActive}
            onWppChange={onInputChange}
            messageType={renderMessageType(isFormActive, reference_name.value, reference_name.error)}
            message={renderMessage(isFormActive, reference_name.value, reference_name.error)}
          >
            {renderInputIcon(reference_name.value, reference_name.error)}
          </WppInput>

          <WppInput
            labelConfig={{
              text: 'Product ID',
              icon: !isFormActive ? undefined : 'wpp-icon-info',
              description:
                'Used to identify products that belong to the same group. If product does not have any variants, enter variant ID here.',
            }}
            data-testid="product-id"
            required
            id={getType<StateToSingleInputFieldMap<'EditProductFormReducerState'>>('product_id')}
            value={product_id.value}
            disabled={!isFormActive}
            onWppChange={onInputChange}
            messageType={renderMessageType(isFormActive, product_id.value, product_id.error)}
            message={renderMessage(isFormActive, product_id.value, product_id.error)}
          >
            {renderInputIcon(product_id.value, product_id.error)}
          </WppInput>
        </div>
        <CustomSelect<(typeof optionsData)[number]>
          name={getTypeKey<EditProductFormReducerState>('category')}
          labelConfig={{
            text: 'Category',
            icon: !isFormActive ? undefined : 'wpp-icon-info',
          }}
          data-testid="category"
          required
          value={Number(category.value)}
          disabled={!isFormActive}
          onWppChange={event => handleSelect(event, getTypeKey<EditProductFormReducerState>('category'))}
          messageType={renderMessageType(isFormActive, category?.value, category?.error)}
          message={renderMessage(isFormActive, category?.value, category?.error)}
          optionsData={optionsData}
          endSlot={renderInputIcon(category.value, category.error)}
          canEdit={areActionsVisible}
        />

        <div className={styles.halfWidth}>
          <WppInput
            labelConfig={{ text: 'Brand' }}
            data-testid="brand"
            required
            id={getType<StateToSingleInputFieldMap<'EditProductFormReducerState'>>('brand')}
            disabled
            value={brand.value}
          />
          <WppInput
            labelConfig={{
              text: 'Image URL',
              icon: !isFormActive ? undefined : 'wpp-icon-info',
              description: 'Only to be used if you have no variant products. This does not get used for AI generation.',
            }}
            data-testid="reference-image-url"
            type="url"
            id={getType<StateToSingleInputFieldMap<'EditProductFormReducerState'>>('reference_image_url')}
            value={reference_image_url.value}
            disabled={!isFormActive}
            onWppChange={onInputChange}
          />
        </div>

        <FormAccordionSection
          text="Core data"
          isFormActive={isFormActive}
          halfWidthChildren={
            <>
              <WppInput
                labelConfig={{ text: 'Product name' }}
                data-testid="product-name"
                required
                id={getType<StateToSingleInputFieldMap<'EditProductFormReducerState'>>('product_name')}
                value={product_name.value}
                disabled={!isFormActive}
                onWppChange={onInputChange}
                messageType={renderMessageType(isFormActive, product_name.value, product_name.error)}
                message={renderMessage(isFormActive, product_name.value, product_name.error)}
              >
                {renderInputIcon(product_name.value, product_name.error)}
              </WppInput>
              <WppInput
                labelConfig={{ text: 'Product type' }}
                data-testid="product-types"
                required
                id={getType<StateToSingleInputFieldMap<'EditProductFormReducerState'>>('product_types')}
                value={product_types.value}
                disabled={!isFormActive}
                onWppChange={onInputChange}
                messageType={renderMessageType(isFormActive, product_types.value, product_types.error)}
                message={renderMessage(isFormActive, product_types.value, product_types.error)}
              >
                {renderInputIcon(product_types.value, product_types.error)}
              </WppInput>
              <WppInput
                labelConfig={{ text: 'Range' }}
                data-testid="range"
                id={getType<StateToSingleInputFieldMap<'EditProductFormReducerState'>>('range')}
                value={range.value}
                disabled={!isFormActive}
                onWppChange={onInputChange}
                messageType={range.error ? 'error' : undefined}
                message={range.error}
              />
            </>
          }
          fullWidthChildren={
            <>
              <TextAreaWithIcon
                value={product_description.value}
                id={getType<StateToSingleInputFieldMap<'EditProductFormReducerState'>>('product_description')}
                renderInputIcon={renderInputIcon(product_description.value, product_description.error)}
                label="Product description"
                disabled={!isFormActive}
                onWppChange={onInputChange}
                isMessageVisible={!product_description.value && isFormActive}
                messageType={renderMessageType(isFormActive, product_description.value, product_description.error)}
                message={renderMessage(isFormActive, product_description.value, product_description.error)}
              />
              <WppTextareaInput
                labelConfig={{ text: 'Product features' }}
                data-testid="product-features"
                id={getType<StateToSingleInputFieldMap<'EditProductFormReducerState'>>('product_features')}
                value={product_features.value}
                disabled={!isFormActive}
                onWppChange={onInputChange}
                rows={1}
                messageType={product_features.error ? 'error' : undefined}
                message={product_features.error}
              />
              <WppTextareaInput
                labelConfig={{ text: 'Instructions' }}
                data-testid="instructions"
                id={getType<StateToSingleInputFieldMap<'EditProductFormReducerState'>>('instructions')}
                value={instructions.value}
                disabled={!isFormActive}
                onWppChange={onInputChange}
                rows={1}
                messageType={instructions.error ? 'error' : undefined}
                message={instructions.error}
              />
            </>
          }
        />
        <FormAccordionSection
          text="Shared attributes"
          tooltip="Only to be used if the same attributes shared across variants in a group. E.g. all products are 13.5 ml volume or all products are the same colour blue."
          isFormActive={isFormActive}
          halfWidthChildren={
            <>
              <WppInput
                labelConfig={{ text: 'Quantity' }}
                data-testid="quantity"
                id={getType<StateToSingleInputFieldMap<'EditProductFormReducerState'>>('quantity')}
                value={quantity.value}
                disabled={!isFormActive}
                onWppChange={onInputChange}
                messageType={quantity.error ? 'error' : undefined}
                message={quantity.error}
              />
              <WppInput
                labelConfig={{ text: 'Size' }}
                data-testid="size"
                id={getType<StateToSingleInputFieldMap<'EditProductFormReducerState'>>('size')}
                value={size.value}
                disabled={!isFormActive}
                onWppChange={onInputChange}
                messageType={size.error ? 'error' : undefined}
                message={size.error}
              />
              <WppInput
                labelConfig={{ text: 'Colour' }}
                data-testid="colour"
                id={getType<StateToSingleInputFieldMap<'EditProductFormReducerState'>>('colour')}
                value={colour.value}
                disabled={!isFormActive}
                onWppChange={onInputChange}
                messageType={colour.error ? 'error' : undefined}
                message={colour.error}
              />
              <WppInput
                labelConfig={{ text: 'Flavour' }}
                data-testid="flavour"
                id={getType<StateToSingleInputFieldMap<'EditProductFormReducerState'>>('flavour')}
                value={flavour.value}
                disabled={!isFormActive}
                onWppChange={onInputChange}
                messageType={flavour.error ? 'error' : undefined}
                message={flavour.error}
              />
              <WppInput
                labelConfig={{ text: 'Format' }}
                data-testid="format"
                id={getType<StateToSingleInputFieldMap<'EditProductFormReducerState'>>('format')}
                value={format.value}
                disabled={!isFormActive}
                onWppChange={onInputChange}
                messageType={format.error ? 'error' : undefined}
                message={format.error}
              />
              <WppInput
                labelConfig={{ text: 'Formulation' }}
                data-testid="formulation"
                id={getType<StateToSingleInputFieldMap<'EditProductFormReducerState'>>('formulation')}
                value={formulation.value}
                disabled={!isFormActive}
                onWppChange={onInputChange}
                messageType={formulation.error ? 'error' : undefined}
                message={formulation.error}
              />
              <WppInput
                labelConfig={{ text: 'Product dimensions' }}
                data-testid="product-dimensions"
                id={getType<StateToSingleInputFieldMap<'EditProductFormReducerState'>>('product_dimensions')}
                value={product_dimensions.value}
                disabled={!isFormActive}
                onWppChange={onInputChange}
                messageType={product_dimensions.error ? 'error' : undefined}
                message={product_dimensions.error}
              />
              <WppInput
                labelConfig={{ text: 'Pack count' }}
                data-testid="pack-count"
                id={getType<StateToSingleInputFieldMap<'EditProductFormReducerState'>>('pack_count')}
                value={pack_count.value}
                disabled={!isFormActive}
                onWppChange={onInputChange}
                messageType={pack_count.error ? 'error' : undefined}
                message={pack_count.error}
              />
              <WppInput
                labelConfig={{ text: 'Model number' }}
                data-testid="model-number"
                id={getType<StateToSingleInputFieldMap<'EditProductFormReducerState'>>('model_number')}
                value={model_number.value}
                disabled={!isFormActive}
                onWppChange={onInputChange}
                messageType={model_number.error ? 'error' : undefined}
                message={model_number.error}
              />
            </>
          }
        />

        <FormAccordionSection
          text="Keywords"
          isFormActive={isFormActive}
          halfWidthChildren={
            <>
              <KeywordInput
                id={getType<StateToMultiInputFieldMap<'EditProductFormReducerState'>>('priority')}
                tooltip="Keywords that should be prioritised in the content to ensure they are prominently featured e.g in the title."
                label="Priority"
                isKeyword
                formReducer={formReducer}
                disabled={!isFormActive}
              />
              <KeywordInput
                id={getType<StateToMultiInputFieldMap<'EditProductFormReducerState'>>('required')}
                tooltip="Keywords that must be included in the content without exception."
                label="Required"
                isKeyword
                formReducer={formReducer}
                disabled={!isFormActive}
              />
              <KeywordInput
                id={getType<StateToMultiInputFieldMap<'EditProductFormReducerState'>>('optional')}
                tooltip="Keywords that can be included if relevant but are not mandatory."
                label="Optional"
                isKeyword
                formReducer={formReducer}
                disabled={!isFormActive}
              />
              <KeywordInput
                id={getType<StateToMultiInputFieldMap<'EditProductFormReducerState'>>('omit')}
                tooltip="Keywords that should be excluded from the content entirely."
                label="Omit"
                isKeyword
                formReducer={formReducer}
                disabled={!isFormActive}
              />
            </>
          }
        />

        <FormAccordionSection
          text="Keyphrases"
          isFormActive={isFormActive}
          halfWidthChildren={
            <>
              <KeywordInput
                id={getType<StateToMultiInputFieldMap<'EditProductFormReducerState'>>('keyphrases_required')}
                tooltip="Keyphrases that must appear in the content exactly as specified."
                label="Required"
                disabled={!isFormActive}
                formReducer={formReducer}
              />
              <KeywordInput
                id={getType<StateToMultiInputFieldMap<'EditProductFormReducerState'>>('keyphrases_optional')}
                tooltip="Keyphrases that may be included in the content, but are not required."
                label="Optional"
                disabled={!isFormActive}
                formReducer={formReducer}
              />
            </>
          }
        />

        <FormAccordionSection
          text="Claims"
          tooltip="Enter scientific or legal claims and their accompanying disclaimers here."
          isFormActive={isFormActive}
          fullWidthChildren={
            <>
              {claims.map((claim, index) => (
                <div className={styles.claimsContainer} key={index}>
                  <WppInput
                    labelConfig={{ text: `Claim ${index + 1}` }}
                    data-testid="claim"
                    name={`claim_${index + 1}`}
                    value={claim.claim}
                    onWppChange={e => {
                      setClaims(prevClaims => {
                        const newClaims = [...prevClaims]
                        newClaims[index] = { ...newClaims[index], claim: e.target.value }
                        return newClaims
                      })
                    }}
                    disabled={!isFormActive}
                  />
                  <WppInput
                    labelConfig={{ text: `Disclaimer ${index + 1}` }}
                    data-testid="disclaimer"
                    name={`disclaimer_${index + 1}`}
                    value={claim.disclaimer}
                    onWppChange={e => {
                      setClaims(prevClaims => {
                        const newClaims = [...prevClaims]
                        newClaims[index] = { ...newClaims[index], disclaimer: e.target.value }
                        return newClaims
                      })
                    }}
                    disabled={!isFormActive}
                  />
                  <WppActionButton
                    disabled={!isFormActive}
                    data-testid="delete-claim"
                    onClick={() => {
                      setClaims(prevClaims => {
                        const newClaims = [...prevClaims]
                        newClaims.splice(index, 1)
                        return newClaims
                      })
                    }}
                  >
                    <WppIconTrash slot="icon-start" />
                  </WppActionButton>
                </div>
              ))}

              <WppActionButton
                onClick={() => setClaims([...claims, { claim: '', disclaimer: '' }])}
                data-testid="add-claim"
                disabled={
                  !isFormActive ||
                  (claims.length > 0 && !claims[claims.length - 1].claim && !claims[claims.length - 1].disclaimer)
                }
              >
                <WppIconAdd slot="icon-start" />
                {claims.length === 0 ? 'Add claim' : 'Add another'}
              </WppActionButton>
            </>
          }
        />
      </div>
      {isProductDataGood && (
        <GenericModal
          onCancel={() => setIsProductDataGood(false)}
          onSubmit={handleSaveProduct}
          modalTitle="Save edits"
          submitButtonText="Save"
          shouldModalOpen={isProductDataGood}
          isApiLoading={isApiLoading || false}
        >
          <WppTypography tag="p" type="s-body" data-testid="modal-disclaimer">
            Are you sure you want to save these edits to this product?
          </WppTypography>
        </GenericModal>
      )}

      {isErrorForm && (
        <GenericModal
          onSubmit={async () => setIsErrorForm(false)}
          modalTitle="Error saving"
          submitButtonText="Ok"
          shouldModalOpen={isErrorForm}
          isApiLoading={false}
        >
          <WppTypography tag="p" type="s-body" data-testid="modal-disclaimer">
            You have mandatory data to input before you are able to save this product.
          </WppTypography>
        </GenericModal>
      )}
    </div>
  )
}
